import {
  Badge,
  BasePagination,
  HStack,
  IconButton,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@elkaso-app/web-design';
import { useDeleteCrmBrandLocationApi } from 'apis/crm-brand-locations/use-delete-crm-brand-location-api';
import {
  ICrmBrandLocationData,
  useGetCrmBrandLocationsApi,
} from 'apis/crm-brand-locations/use-get-crm-brand-locations-api';
import { BaseSortButton, BaseTable, BaseTableContainer } from 'components/base-table';
import {
  BaseTableSpinner,
  BaseTableTitle,
  ClearFilterButton,
  CustomTableCell,
  TableHeader,
} from 'components/base-table/components';
import { useSetPaginationParamsV1 } from 'hooks/use-set-pagination-params-v1';
import React, { useEffect, useState } from 'react';
import { FiEdit3, FiEye, FiMapPin, FiTrash2 } from 'react-icons/fi';
import { CellProps } from 'react-table';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';
import { BrandFilterButton } from '../components/filters/brand-filter';
import { LocationNameFilterButton } from '../components/filters/location-name-filter';
import DeleteConfirmationModal from '../components/modals/delete-confirmation-modal';
import EditBrandLocationModal from '../components/modals/edit-brand-location-modal';

// Components for table cells
const LocationInfoCell: React.FC<{
  name: string;
  address: string;
  area: string;
}> = ({ name, address, area }) => (
  <div style={{ height: '80px', display: 'flex', alignItems: 'center', padding: '8px 0' }}>
    <VStack align='start' spacing={1}>
      <HStack spacing={2}>
        <FiMapPin color='#3182CE' size={16} />
        <Text fontWeight='semibold' fontSize='md' color='gray.800' lineHeight='1.2'>
          {name}
        </Text>
      </HStack>
      {address && (
        <Text fontSize='sm' color='gray.600' lineHeight='1.2' maxW='300px' noOfLines={2}>
          {address}
        </Text>
      )}
      {area && (
        <Badge colorScheme='blue' variant='subtle' size='sm'>
          {area}
        </Badge>
      )}
    </VStack>
  </div>
);

const BrandInfoCell: React.FC<{
  name: string;
  image: string;
}> = ({ name, image }) => (
  <div style={{ height: '80px', display: 'flex', alignItems: 'center', padding: '8px 0' }}>
    <HStack spacing={3}>
      {image && (
        <div style={{ width: '40px', height: '40px', borderRadius: '8px', overflow: 'hidden' }}>
          <img
            src={image}
            alt={name}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        </div>
      )}
      <VStack align='start' spacing={1}>
        <Text fontWeight='medium' fontSize='md' color='gray.800' lineHeight='1.2'>
          {name}
        </Text>
      </VStack>
    </HStack>
  </div>
);

const LocationTypeCell: React.FC<{ type: string }> = ({ type }) => {
  const getTypeInfo = (locationType: string) => {
    switch (locationType) {
      case 'physical_location':
        return { label: 'Physical Location', color: 'green' };
      case 'cloud_kitchen':
        return { label: 'Cloud Kitchen', color: 'purple' };
      default:
        return { label: 'Unknown', color: 'gray' };
    }
  };

  const { label, color } = getTypeInfo(type);

  return (
    <div style={{ height: '80px', display: 'flex', alignItems: 'center', padding: '8px 0' }}>
      <Badge colorScheme={color as any} variant='subtle' size='md' borderRadius='md'>
        {label}
      </Badge>
    </div>
  );
};

const ContactInfoCell: React.FC<{
  landlineNumber: string;
  geoLocation: { name: string } | null;
}> = ({ landlineNumber, geoLocation }) => (
  <div style={{ height: '80px', display: 'flex', alignItems: 'center', padding: '8px 0' }}>
    <VStack align='start' spacing={1}>
      {landlineNumber && (
        <Text fontSize='sm' color='gray.700'>
          📞 {landlineNumber}
        </Text>
      )}
      {geoLocation && (
        <Text fontSize='sm' color='gray.600'>
          📍 {geoLocation.name}
        </Text>
      )}
    </VStack>
  </div>
);

const ActionsCell: React.FC<{
  locationId: number;
  locationName: string;
  onView: (locationId: number) => void;
  onEdit: (locationId: number) => void;
  onDelete: (locationId: number, locationName: string) => void;
}> = ({ locationId, locationName, onView, onEdit, onDelete }) => {
  return (
    <div
      style={{
        height: '80px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '8px 0',
        gap: '8px',
      }}>
      <Tooltip label={`View ${locationName} details`}>
        <IconButton
          aria-label={`View ${locationName} details`}
          icon={<FiEye />}
          size='sm'
          colorScheme='blue'
          variant='outline'
          onClick={() => onView(locationId)}
        />
      </Tooltip>
      <Tooltip label={`Edit ${locationName}`}>
        <IconButton
          aria-label={`Edit ${locationName}`}
          icon={<FiEdit3 />}
          size='sm'
          colorScheme='green'
          variant='outline'
          onClick={() => onEdit(locationId)}
        />
      </Tooltip>
      <Tooltip label={`Delete ${locationName}`}>
        <IconButton
          aria-label={`Delete ${locationName}`}
          icon={<FiTrash2 />}
          size='sm'
          colorScheme='red'
          variant='outline'
          onClick={() => onDelete(locationId, locationName)}
        />
      </Tooltip>
    </div>
  );
};

const CrmBrandLocationsListScreen = () => {
  const setPaginationParams = useSetPaginationParamsV1();
  const dispatch = useAppDispatch();

  // State for search and modals
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
  const [selectedLocationForDelete, setSelectedLocationForDelete] = useState<{
    id: number;
    name: string;
  } | null>(null);

  // Modal controls
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

  // API hooks
  const { isLoading, isFetching, data } = useGetCrmBrandLocationsApi();
  const deleteLocationMutation = useDeleteCrmBrandLocationApi();

  const { pageCount, total, data: locationsData } = data ?? {};

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(debouncedSearchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [debouncedSearchTerm]);

  // Filter data based on search
  const getFilteredData = () => {
    if (!locationsData) return [];

    if (debouncedSearchTerm) {
      return locationsData.filter(
        (location) =>
          location.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
          location.brand.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      );
    }

    return locationsData;
  };

  const filteredData = getFilteredData();

  const handleViewLocation = (locationId: number) => {
    // TODO: Implement view location details functionality
    console.log('View location:', locationId);
  };

  const handleEditLocation = (locationId: number) => {
    setSelectedLocationId(locationId);
    onEditOpen();
  };

  const handleDeleteLocation = (locationId: number, locationName: string) => {
    setSelectedLocationForDelete({ id: locationId, name: locationName });
    onDeleteOpen();
  };

  const confirmDeleteLocation = async () => {
    if (selectedLocationForDelete) {
      try {
        await deleteLocationMutation.mutateAsync(selectedLocationForDelete.id);
        onDeleteClose();
        setSelectedLocationForDelete(null);
      } catch (error) {
        // Error is handled in the API hook
      }
    }
  };

  // Set page title
  useEffect(() => {
    dispatch(changeTitle('CRM Brand Locations'));
  }, [dispatch]);

  const columns = React.useMemo(
    () => [
      {
        Header: (
          <TableHeader
            name='Location'
            sortButtonComponent={<BaseSortButton sortBy='name' />}
            filterButtonComponent={<LocationNameFilterButton />}
          />
        ),
        accessor: 'location',
        width: '30%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={LocationInfoCell} {...value} />,
      },
      {
        Header: <TableHeader name='Brand' filterButtonComponent={<BrandFilterButton />} />,
        accessor: 'brand',
        width: '25%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={BrandInfoCell} {...value} />,
      },
      {
        Header: <TableHeader name='Type' />,
        accessor: 'type',
        width: '15%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={LocationTypeCell} {...value} />,
      },
      {
        Header: <TableHeader name='Contact Info' />,
        accessor: 'contact',
        width: '20%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={ContactInfoCell} {...value} />,
      },
      {
        Header: <TableHeader name='Actions' />,
        accessor: 'actions',
        width: '10%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={ActionsCell} {...value} />,
      },
    ],
    []
  );

  const tableData = React.useMemo(
    () =>
      filteredData?.map((item: ICrmBrandLocationData) => ({
        id: item.id,
        location: {
          name: item.name,
          address: item.address,
          area: item.area,
        },
        brand: {
          name: item.brand.name,
          image: item.brand.image,
        },
        type: {
          type: item.type,
        },
        contact: {
          landlineNumber: item.landlineNumber,
          geoLocation: item.geoLocation,
        },
        actions: {
          locationId: item.id,
          locationName: item.name,
          onView: handleViewLocation,
          onEdit: handleEditLocation,
          onDelete: handleDeleteLocation,
        },
      })) || [],
    [filteredData]
  );

  return (
    <>
      <BaseTableContainer isLoading={isLoading}>
        <BaseTableContainer.Header>
          <BaseTableContainer.Header.Left>
            <BaseTableTitle title='CRM Brand Locations' />
            <BaseTableSpinner isFetching={isFetching} />
          </BaseTableContainer.Header.Left>

          <BaseTableContainer.Header.Right>
            <ClearFilterButton />
          </BaseTableContainer.Header.Right>
        </BaseTableContainer.Header>

        <BaseTableContainer.TableWrapper>
          <BaseTable columns={columns} data={tableData} />
        </BaseTableContainer.TableWrapper>

        <BaseTableContainer.Footer>
          <BaseTableContainer.Footer.Left />
          <BaseTableContainer.Footer.Right>
            <BasePagination>
              <BasePagination.Pages pagesCount={pageCount ?? 0} setSearchParams={setPaginationParams} />
              <BasePagination.Limit total={total ?? 0} setSearchParams={setPaginationParams} />
            </BasePagination>
          </BaseTableContainer.Footer.Right>
        </BaseTableContainer.Footer>
      </BaseTableContainer>

      {selectedLocationId && (
        <EditBrandLocationModal
          isOpen={isEditOpen}
          onClose={() => {
            onEditClose();
            setSelectedLocationId(null);
          }}
          locationId={selectedLocationId}
        />
      )}

      {selectedLocationForDelete && (
        <DeleteConfirmationModal
          isOpen={isDeleteOpen}
          onClose={() => {
            onDeleteClose();
            setSelectedLocationForDelete(null);
          }}
          onConfirm={confirmDeleteLocation}
          itemName={selectedLocationForDelete.name}
          isLoading={deleteLocationMutation.isLoading}
        />
      )}
    </>
  );
};

export default CrmBrandLocationsListScreen;
